import CustomButton from 'Components/CustomButton'
import CustomDialog from 'Components/CustomDialog/CustomDialog'
import type { AffiliateNotificationDetailShape } from 'Services/SalesManagement/Features/Notification/notificationSlice'
import { SEVERITY_LEVELS } from 'Util/constants'
import { dateFormatter } from 'Util/formatters'
import InfoIcon from '@mui/icons-material/Info'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Typography
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import background from 'assets/images/iphone-11.jpg'
import { Markup } from 'interweave'
import { type FC, useEffect, useState } from 'react'
import { t } from 'ttag'

const NotificationBackground = styled('div')(() => ({
  height: '420px',
  backgroundImage: `url(${background})`,
  backgroundSize: '100% 100%'
}))

const NotificationContainer = styled('div')(() => ({
  width: '100%',
  height: '318px',
  position: 'absolute',
  top: '53px',
  overflow: 'auto'
}))

const NotificationPreview = styled('div')(() => ({
  padding: '5px',
  fontSize: '10px',
  fontFamily: 'Poppins',
  color: 'black',
  a: {
    color: '#007ac9',
    textDecoration: 'underline'
  },
  h1: {
    fontSize: '16px',
    marginBottom: '10px'
  },
  h2: {
    fontSize: '14px',
    marginBottom: '5px'
  },
  h3: {
    fontSize: '16px',
    marginBottom: 0
  },
  blockquote: {
    backgroundColor: '#dadada',
    marginLeft: '10px',
    marginRight: '10px',
    padding: '5px'
  },
  pre: {
    fontFamily: `'Courier New', Courier, monospace`,
    backgroundColor: '#dadada',
    marginLeft: '10px',
    marginRight: '10px',
    paddingLeft: '5px',
    overflowX: 'clip'
  },
  code: {
    fontFamily: `'Courier New', Courier, monospace`,
    backgroundColor: '#dadada',
    paddingLeft: '4px',
    paddingRight: '4px'
  },
  ul: {
    listStyle: 'disc'
  },
  hr: {
    marginTop: '20px',
    marginBottom: '10px'
  },
  '.image_resized': {
    height: 'auto'
  },
  '.italic': {
    fontStyle: 'italic'
  },
  '.bold': {
    fontWeight: 'bold'
  }
}))

type Lang = 'fi' | 'sv' | 'en'

interface PreviewContent {
  title: string
  content: string
}

function convertTypeToColor(type: string) {
  switch (type) {
    case 'info':
      return SEVERITY_LEVELS.SUCCESS
    case 'warning':
      return SEVERITY_LEVELS.ERROR
    case 'admonition':
      return SEVERITY_LEVELS.WARNING
    default:
      return SEVERITY_LEVELS.SUCCESS
  }
}

const AffiliateNotificationPreviewDialog: FC<{
  open: boolean
  handleClose: () => void
  handleSubmit: () => void
  loading: boolean
  isNewInstance: boolean
  notificationDetails: AffiliateNotificationDetailShape
}> = ({ open, handleClose, handleSubmit, loading, isNewInstance, notificationDetails }) => {
  const [language, setLanguage] = useState<Lang>('fi')
  const [previewContent, setPreviewContent] = useState<PreviewContent>({
    title: notificationDetails.title_fi,
    content: notificationDetails.content_fi
  })
  const theme = useTheme()

  useEffect(() => {
    setLanguage('fi')
    setPreviewContent({
      title: notificationDetails.title_fi,
      content: notificationDetails.content_fi
    })
  }, [notificationDetails.content_fi, open])

  const setContentLanguage = (lang: Lang) => {
    switch (lang) {
      case 'fi':
        setLanguage('fi')
        setPreviewContent({
          title: notificationDetails.title_fi,
          content: notificationDetails.content_fi
        })
        break
      case 'sv':
        setLanguage('sv')
        setPreviewContent({
          title: notificationDetails.title_sv,
          content: notificationDetails.content_sv
        })
        break
      case 'en':
        setLanguage('en')
        setPreviewContent({
          title: notificationDetails.title_en,
          content: notificationDetails.content_en
        })
        break
      default:
        setLanguage('fi')
        setPreviewContent({
          title: notificationDetails.title_fi,
          content: notificationDetails.content_fi
        })
    }
  }

  return (
    <CustomDialog open={open} handleClose={handleClose}>
      <DialogTitle id="dialog-title">
        {isNewInstance ? t`Create notification` : t`Change notification`}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              <Typography component="span" color="text.primary">
                {isNewInstance
                  ? t`Are you sure you want to create a new notification?`
                  : t`Are you sure you want to save the notification changes?`}
              </Typography>
            </DialogContentText>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                width: '100%',
                py: 2
              }}
            >
              <Button
                variant="contained"
                color={language === 'fi' ? 'primary' : 'secondary'}
                onClick={() => setContentLanguage('fi')}
              >
                FI
              </Button>
              <Button
                variant="contained"
                color={language === 'sv' ? 'primary' : 'secondary'}
                onClick={() => setContentLanguage('sv')}
              >
                SV
              </Button>
              <Button
                variant="contained"
                color={language === 'en' ? 'primary' : 'secondary'}
                onClick={() => setContentLanguage('en')}
              >
                EN
              </Button>
            </Box>
            <div className="smartphone">
              <div className="content">
                <NotificationBackground>
                  <NotificationContainer>
                    <Typography
                      variant="h3"
                      sx={{ fontFamily: 'Poppins', p: 0.5, fontSize: 12, fontWeight: 'medium' }}
                    >
                      {previewContent.title}
                    </Typography>
                    {notificationDetails.active_from !== null && (
                      <Box sx={{ px: 0.5, fontSize: 10, fontFamily: 'Poppins' }}>
                        <InfoIcon
                          fontSize="inherit"
                          color={convertTypeToColor(notificationDetails.type)}
                          sx={{ mr: 0.7 }}
                        />
                        {dateFormatter(new Date(notificationDetails.active_from), 'd.M.yyyy')}
                      </Box>
                    )}
                    <NotificationPreview>
                      <Markup content={previewContent.content} />
                    </NotificationPreview>
                  </NotificationContainer>
                </NotificationBackground>
              </div>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          sx={{ color: theme.palette.text.primary }}
          onClick={handleClose}
        >{t`Cancel`}</Button>
        <CustomButton onClick={handleSubmit} disabled={loading} autoFocus>
          {t`Save`}
        </CustomButton>
      </DialogActions>
    </CustomDialog>
  )
}

export { AffiliateNotificationPreviewDialog }
